/**
 * What assistive technology hears, and where the focus goes when its control goes (part 11c, table A: A13, A24;
 * decision 71: status announced once without chatter, focus never lost).
 */
import type { AutosaveStatus } from '@cockatrice/editor';
import { createEffect, createMemo, createSignal, on, onCleanup } from 'solid-js';
import { el } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import { focusAfterRemoval } from './chapter-list.ts';
import type { Shell } from './shell.ts';

/** How long a piece of news stays in a live region before it is cleared (part 11c, deep-fix r4). */
export const ANNOUNCE_MS = 5000;

/**
 * A13: a live region for `what` (named for tests; polite, or assertive as an `alert`), hidden from sight, in the page
 * from the start. Announcements are transient (the user's choice, 2026-10-07, after deep-fix r4): each change of `text`
 * is written to the region and cleared {@link ANNOUNCE_MS} later, so no region ever stands holding old news, to be read
 * again as a modal opens or closes over it or as it is made; what the news says stays on screen as plain text. The
 * text standing when the region is made says nothing, and so does the same text given again.
 */
export function Announcer(what: string, text: () => string, role: 'status' | 'alert' = 'status'): Child {
  const [said, setSaid] = createSignal('');
  let clearing: ReturnType<typeof setTimeout> | undefined;
  // Deep-fix r5: only a new text is news; a job's progress ticks give the same phase again and again.
  const news = createMemo(text);
  createEffect(
    on(
      news,
      (now) => {
        clearTimeout(clearing);
        setSaid(now);
        clearing = now === '' ? undefined : setTimeout(() => setSaid(''), ANNOUNCE_MS);
      },
      { defer: true },
    ),
  );
  onCleanup(() => {
    clearTimeout(clearing);
  });
  return el('p', { class: 'visually-hidden announcer', 'data-announces': what, role }, said);
}

/** A13: what the save state has to say: nothing, that saving fails, or that it works again after failing. */
export type SaveNews = 'none' | 'failed' | 'recovered';

/**
 * A13 (principle 3): the save state's news after `status`, from the news before. Typing's own round (not saved yet,
 * saving, saved) is news to nobody; a failure is, and so is the first save after it.
 */
export function saveNews(before: SaveNews, status: AutosaveStatus | null): SaveNews {
  if (status === 'failed') return 'failed';
  if (before === 'failed' && (status === 'saved' || status === 'closed')) return 'recovered';
  return before;
}

/** The words of the save state's news. */
export function saveNewsText(shell: Shell, news: SaveNews): string {
  switch (news) {
    case 'none':
      return '';
    case 'failed':
      return shell.t('status.failed');
    case 'recovered':
      return shell.t('status.saved');
  }
}

/**
 * Part 11c, deep-fix r2 (table A24b): whether `element`, which had the focus, can no longer hold it: removed from the
 * page, disabled, or under a hidden or inert element.
 */
export function cannotHoldFocus(element: Element): boolean {
  return !element.isConnected || Reflect.get(element, 'disabled') === true || element.closest('[hidden], [inert]') !== null;
}

/** Whether the focus went with the element that had it (the page holds it, or nothing does). */
export function focusLost(): boolean {
  const active = document.activeElement;
  return active === null || active === document.body;
}

/**
 * A24: puts the focus on the element `selector` finds when it can take it (in the page, not disabled), else where
 * removing a control leaves it (the open chapter's text, or New chapter).
 */
export function focusOn(shell: Shell, selector: string): void {
  const found = document.querySelector(selector);
  // Part 11f (table M): an action in a closed menu hands the focus to the menu's button, the one that controls its
  // list (the library's Menu: `aria-controls`).
  const menu = found?.closest('[role="menu"][hidden]') ?? null;
  const target = menu === null ? found : document.querySelector(`[aria-controls="${menu.id}"]`);
  if (target instanceof HTMLElement && !(target instanceof HTMLButtonElement && target.disabled)) {
    target.focus();
    if (document.activeElement === target) return;
  }
  focusAfterRemoval(shell);
}
